// Téléchargement initial (et rafraîchissements) des données utiles hors ligne.
// Tout passe par `api.get`: la passerelle garde chaque réponse sur l'appareil.
import { lireEntites } from './store';
import { attendreEcranLibre, ceder } from './arrierePlan';

const TAILLE_DE_PAGE = 100;
const PAGES_MAX = 60;

/** Listes lues par les écrans (la liste complète, toutes pages). */
export const LISTES = [
  '/trucks', '/drivers', '/clients', '/companies', '/operations', '/orders', '/trips', '/trips/active',
  '/invoices', '/stock', '/pricing-rules', '/stations', '/geo-locations', '/corridors',
  '/maintenance/work-orders', '/maintenance/breakdowns', '/maintenance/immobilizations',
  '/maintenance/plans', '/maintenance/documents', '/maintenance/readings',
  '/documents', '/finance/expenses', '/rentals/contracts', '/shortages',
  '/volume-reconciliations', '/notifications',
];

/** Réponses uniques (agrégats, réglages) lues telles quelles. */
export const UNIQUES = [
  '/users/me/profile', '/users/me/preferences', '/stock/summary', '/stock/readiness', '/maintenance/overview',
  '/maintenance/kpis', '/maintenance/readings/latest', '/alerts/events/summary', '/orders/to-settle',
  '/dashboard/stats', '/finance/summary', '/finance/journal-overview',
  '/org-settings/volume-units', '/org-settings/blocking-policy', '/org-settings/workflow-gates',
  '/freight/tariff', '/fleet/economics', '/rentals/kpis', '/ecarbu-score', '/transport/kanban',
  '/tracking/fleet/live', '/organizations/me/fiscal-year', '/finance/accounts',
];

const AVEC_PARAMETRES = [
  ['/alerts/events', { status: 'open' }],
  ['/alerts/events', { status: 'resolved' }],
];

const SOUS_CHEMINS_TRAJET = ['updates', 'allocations', 'economics', 'workflow', 'workflow/actions', 'freight'];
const SOUS_CHEMINS_CAMION = ['profile', 'economics', 'charge-rules', 'asset'];
// Montants d'un camion: ils se lisent pour une société, pas pour le super administrateur.
const SOUS_CHEMINS_CAMION_SOCIETE = ['economics', 'charge-rules', 'asset'];

const societeDuJeton = () => {
  try {
    const jeton = localStorage.getItem('authToken') || '';
    const societe = JSON.parse(atob(jeton.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).org_id;
    return societe && societe !== 'DEFAULT_ORG' ? societe : null;
  } catch (erreur) {
    return null;
  }
};
const SOUS_CHEMINS_COMMANDE = ['allocations', 'dispatch-readiness', 'available-trucks'];

// Le fond reste discret: peu de requêtes à la fois, chacune après les requêtes
// de l'écran, et une pause du fil principal entre deux.
const lireEnParallele = async (taches, concurrence = 2) => {
  const file = [...taches];
  const travailleurs = Array.from({ length: Math.min(concurrence, file.length) }, async () => {
    while (file.length) {
      const tache = file.shift();
      await attendreEcranLibre();
      try {
        await tache();
      } catch (erreur) {
        // Un module non activé ou un droit manquant ne bloque pas le reste.
      }
      await ceder();
    }
  });
  await Promise.all(travailleurs);
};

async function toutesLesPages(api, chemin, parametres = {}) {
  const vus = new Set();
  for (let page = 1; page <= PAGES_MAX; page += 1) {
    if (page > 1) {
      await attendreEcranLibre();
      await ceder();
    }
    const reponse = await api.get(chemin, {
      params: { ...parametres, page, page_size: TAILLE_DE_PAGE },
      skipToast: true,
      reseauDabord: true,
      arrierePlan: true,
    });
    const corps = reponse.data;
    const lignes = Array.isArray(corps) ? corps : Array.isArray(corps?.data) ? corps.data : [];
    let nouveaux = 0;
    lignes.forEach((ligne) => {
      const cle = ligne?.id ?? JSON.stringify(ligne);
      if (!vus.has(cle)) {
        vus.add(cle);
        nouveaux += 1;
      }
    });
    const meta = corps?.meta || corps?.pagination;
    const suite = meta?.has_next ?? meta?.has_more;
    if (nouveaux === 0) break;
    if (suite === false) break;
    if (suite === undefined && lignes.length < TAILLE_DE_PAGE) break;
  }
}

export async function telechargerListes(api) {
  await lireEnParallele([
    ...LISTES.map((chemin) => () => toutesLesPages(api, chemin)),
    ...AVEC_PARAMETRES.map(([chemin, parametres]) => () => toutesLesPages(api, chemin, parametres)),
  ]);
}

export async function telechargerUniques(api) {
  await lireEnParallele([
    ...UNIQUES.map((chemin) => () => api.get(chemin, { skipToast: true, reseauDabord: true, arrierePlan: true })),
    // Le cockpit direction, avec les scores et preuves nécessaires à son recalcul local.
    () => api.get('/dashboard/business-cockpit', { params: { include_inputs: true }, skipToast: true, reseauDabord: true, arrierePlan: true }),
  ]);
}

export async function telechargerDetails(api) {
  const taches = [];
  const termines = ['COMPLETED', 'CANCELLED'];
  const trajets = (await lireEntites('/trips')).filter((t) => !termines.includes(String(t.status || '').toUpperCase()));
  trajets.slice(0, 40).forEach((trajet) =>
    SOUS_CHEMINS_TRAJET.forEach((suffixe) =>
      taches.push(() => api.get(`/trips/${trajet.id}/${suffixe}`, { skipToast: true, reseauDabord: true, arrierePlan: true })),
    ),
  );
  const camions = (await lireEntites('/trucks')).slice(0, 80);
  const sousCheminsCamion = societeDuJeton()
    ? SOUS_CHEMINS_CAMION
    : SOUS_CHEMINS_CAMION.filter((suffixe) => !SOUS_CHEMINS_CAMION_SOCIETE.includes(suffixe));
  camions.forEach((camion) =>
    sousCheminsCamion
      // Un camion de transporteur partenaire (lot attribué) n'a pas de compte d'exploitation : le serveur répond 404.
      .filter((suffixe) => !(camion.partner_truck && suffixe === 'economics'))
      .forEach((suffixe) =>
        taches.push(() => api.get(`/trucks/${camion.id}/${suffixe}`, { skipToast: true, reseauDabord: true, arrierePlan: true })),
      ),
  );
  const ouvertes = (await lireEntites('/orders')).filter(
    (c) => !['DELIVERED', 'INVOICED', 'PAID', 'CANCELLED'].includes(String(c.status || '').toUpperCase()),
  );
  ouvertes.slice(0, 40).forEach((commande) =>
    SOUS_CHEMINS_COMMANDE.forEach((suffixe) =>
      taches.push(() => api.get(`/orders/${commande.id}/${suffixe}`, { skipToast: true, reseauDabord: true, arrierePlan: true })),
    ),
  );
  // Le fil d'observations d'un trajet ou d'un camion.
  trajets.slice(0, 40).forEach((trajet) =>
    taches.push(() =>
      api.get('/observations', { params: { entity_type: 'trip', entity_id: trajet.id, limit: 100, offset: 0 }, skipToast: true, reseauDabord: true, arrierePlan: true }),
    ),
  );
  camions.forEach((camion) =>
    taches.push(() =>
      api.get('/observations', { params: { entity_type: 'truck', entity_id: camion.id, limit: 100, offset: 0 }, skipToast: true, reseauDabord: true, arrierePlan: true }),
    ),
  );
  await lireEnParallele(taches, 3);
}
